import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight, TrendingUp, Shield, Zap, Trophy, Users, ChartBar, Lock, Check, Menu, X, Calendar } from "lucide-react";
import { Button } from "../components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { useAuth, API } from "../App";
import { toast } from "sonner";
import axios from "axios";
import SEO from "../components/SEO";
import ProfitChart from "../components/ProfitChart";
import { fetchWithRetry } from "../utils/apiService";

const Navbar = ({ onLoginClick, onRegisterClick }) => {
  const { user, logout } = useAuth();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const navigate = useNavigate();

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 nav-blur bg-hsl-bg/80 border-b border-white/5">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          <Link to="/" className="flex items-center gap-2" data-testid="logo-link">
            <img src="/hsl-logo.png" alt="HSL.bet" className="h-12 w-auto" />
          </Link>

          {/* Desktop Nav */}
          <div className="hidden md:flex items-center gap-8">
            <Link to="/dashboard" className="text-zinc-400 hover:text-hsl-primary transition-colors text-sm font-semibold uppercase tracking-wide" data-testid="nav-picks">
              Picks
            </Link>
            <Link to="/track-record" className="text-zinc-400 hover:text-hsl-primary transition-colors text-sm font-semibold uppercase tracking-wide" data-testid="nav-track-record">
              Results
            </Link>
            <Link to="/pricing" className="text-zinc-400 hover:text-hsl-primary transition-colors text-sm font-semibold uppercase tracking-wide" data-testid="nav-pricing">
              Pricing
            </Link>
            <Link to="/hammer-talk" className="text-zinc-400 hover:text-hsl-primary transition-colors text-sm font-semibold uppercase tracking-wide" data-testid="nav-hammer-talk">
              Hammer Talk
            </Link>
          </div>

          <div className="hidden md:flex items-center gap-4">
            {user ? (
              <>
                <span className="text-zinc-400 text-sm">Welcome, <span className="text-white">{user.name}</span></span>
                <Button
                  variant="ghost"
                  onClick={logout}
                  className="text-zinc-400 hover:text-white"
                  data-testid="logout-btn"
                >
                  Logout
                </Button>
              </>
            ) : (
              <>
                <Button
                  variant="ghost"
                  onClick={onLoginClick}
                  className="text-zinc-400 hover:text-white"
                  data-testid="login-btn"
                >
                  Login
                </Button>
                <Button
                  onClick={onRegisterClick}
                  className="btn-primary-glow px-6 py-2 rounded-sm"
                  data-testid="signup-btn"
                >
                  Get Started
                </Button>
              </>
            )}
          </div>

          {/* Mobile menu button */}
          <button
            className="md:hidden text-white"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            data-testid="mobile-menu-btn"
          >
            {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>

        {/* Mobile Nav */}
        {mobileMenuOpen && (
          <div className="md:hidden py-4 border-t border-white/5">
            <div className="flex flex-col gap-4">
              <Link to="/dashboard" className="text-zinc-400 hover:text-white py-2" onClick={() => setMobileMenuOpen(false)}>Picks</Link>
              <Link to="/track-record" className="text-zinc-400 hover:text-white py-2" onClick={() => setMobileMenuOpen(false)}>Results</Link>
              <Link to="/pricing" className="text-zinc-400 hover:text-white py-2" onClick={() => setMobileMenuOpen(false)}>Pricing</Link>
              <Link to="/hammer-talk" className="text-zinc-400 hover:text-white py-2" onClick={() => setMobileMenuOpen(false)}>Hammer Talk</Link>
              {user ? (
                <Button variant="ghost" onClick={() => { logout(); setMobileMenuOpen(false); }} className="justify-start px-0">Logout</Button>
              ) : (
                <div className="flex gap-2 pt-2">
                  <Button variant="outline" onClick={() => { onLoginClick(); setMobileMenuOpen(false); }} className="flex-1">Login</Button>
                  <Button onClick={() => { onRegisterClick(); setMobileMenuOpen(false); }} className="flex-1 btn-primary-glow">Sign Up</Button>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

const AuthModal = ({ isOpen, onClose, mode, setMode }) => {
  const { login, register } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({ email: "", password: "", name: "" });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (mode === "login") {
        await login(formData.email, formData.password);
        toast.success("Welcome back!");
      } else {
        await register(formData.email, formData.password, formData.name);
        toast.success("Account created successfully!");
      }
      onClose();
      navigate("/dashboard");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Authentication failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="bg-hsl-surface border border-white/10 sm:max-w-md" data-testid="auth-modal">
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl uppercase tracking-tight">
            {mode === "login" ? "Welcome Back" : "Join HSL"}
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4 pt-4">
          {mode === "register" && (
            <div>
              <Label htmlFor="name" className="text-zinc-400 text-sm">Name</Label>
              <Input
                id="name"
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="input-hsl mt-1"
                placeholder="Your name"
                required
                data-testid="auth-name-input"
              />
            </div>
          )}
          <div>
            <Label htmlFor="email" className="text-zinc-400 text-sm">Email</Label>
            <Input
              id="email"
              type="email"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              className="input-hsl mt-1"
              placeholder="you@example.com"
              required
              data-testid="auth-email-input"
            />
          </div>
          <div>
            <Label htmlFor="password" className="text-zinc-400 text-sm">Password</Label>
            <Input
              id="password"
              type="password"
              value={formData.password}
              onChange={(e) => setFormData({ ...formData, password: e.target.value })}
              className="input-hsl mt-1"
              placeholder="••••••••"
              required
              minLength={6}
              data-testid="auth-password-input"
            />
          </div>
          <Button
            type="submit"
            disabled={loading}
            className="w-full btn-primary-glow py-3 rounded-sm"
            data-testid="auth-submit-btn"
          >
            {loading ? "Loading..." : mode === "login" ? "Sign In" : "Create Account"}
          </Button>
          <p className="text-center text-zinc-500 text-sm">
            {mode === "login" ? "Don't have an account? " : "Already have an account? "}
            <button
              type="button"
              onClick={() => setMode(mode === "login" ? "register" : "login")}
              className="text-hsl-primary hover:underline"
              data-testid="auth-switch-mode"
            >
              {mode === "login" ? "Sign up" : "Sign in"}
            </button>
          </p>
        </form>
      </DialogContent>
    </Dialog>
  );
};

// Hot Streaks Display Component - Shows ALL hot streaks at once
const HotStreaksDisplay = ({ streakData, compact = false }) => {
  // Get all displayable streaks
  const getDisplayableStreaks = () => {
    if (!streakData) return [];
    
    const streaks = [];
    
    // Add hot streaks (already sorted by quality)
    if (streakData.hot_streaks && streakData.hot_streaks.length > 0) {
      streaks.push(...streakData.hot_streaks);
    }
    
    // If no hot streaks but overall is decent, add it
    if (streaks.length === 0 && streakData.overall && streakData.overall.win_rate >= 55) {
      streaks.push(streakData.overall);
    }
    
    return streaks.slice(0, 6); // Max 6 streaks
  };
  
  const displayStreaks = getDisplayableStreaks();
  
  if (displayStreaks.length === 0) return null;
  
  const formatProfit = (profit) => {
    if (profit >= 0) return `+$${profit.toLocaleString()}`;
    return `-$${Math.abs(profit).toLocaleString()}`;
  };
  
  // Compact version for other pages
  if (compact) {
    return (
      <div className="mb-6">
        <h3 className="text-center font-heading text-lg font-bold uppercase text-white mb-4 flex items-center justify-center gap-2">
          <span className="text-orange-400">🔥</span> HSL Hot Streaks
        </h3>
        <div className="flex flex-wrap justify-center gap-3">
          {displayStreaks.map((streak) => (
            <div
              key={streak.label}
              className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg border ${
                streak.is_hot 
                  ? 'bg-gradient-to-r from-orange-500/10 to-red-500/10 border-orange-500/30' 
                  : 'bg-zinc-800/50 border-zinc-700'
              }`}
            >
              {streak.is_hot && <span className="text-lg">🔥</span>}
              <div>
                <p className="text-zinc-400 text-xs">{streak.label}</p>
                <p className={`font-mono text-sm font-bold ${streak.is_hot ? 'text-orange-400' : 'text-green-400'}`}>
                  {streak.wins}-{streak.losses} <span className="text-green-400">{formatProfit(streak.profit)}</span>
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }
  
  // Full version for homepage
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.6, duration: 0.4 }}
      className="mb-8"
    >
      {/* Section Title */}
      <h3 className="text-center font-heading text-xl font-bold uppercase text-white mb-6 flex items-center justify-center gap-2">
        <motion.span 
          className="text-2xl"
          animate={{ scale: [1, 1.1, 1] }}
          transition={{ duration: 1, repeat: Infinity, repeatDelay: 2 }}
        >
          🔥
        </motion.span>
        <span className="bg-gradient-to-r from-orange-400 to-red-400 bg-clip-text text-transparent">
          HSL Hot Streaks
        </span>
        <motion.span 
          className="text-2xl"
          animate={{ scale: [1, 1.1, 1] }}
          transition={{ duration: 1, repeat: Infinity, repeatDelay: 2 }}
        >
          🔥
        </motion.span>
      </h3>
      
      {/* Streaks Grid */}
      <div className="flex flex-wrap justify-center gap-4">
        {displayStreaks.map((streak) => (
          <motion.div
            key={streak.label}
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.1 * displayStreaks.indexOf(streak) }}
            className={`inline-flex items-center gap-3 px-5 py-3 rounded-xl border ${
              streak.is_hot 
                ? 'bg-gradient-to-r from-orange-500/20 to-red-500/20 border-orange-500/50' 
                : streak.win_rate >= 60
                ? 'bg-gradient-to-r from-green-500/10 to-emerald-500/10 border-green-500/30'
                : 'bg-zinc-800/50 border-zinc-700'
            }`}
          >
            {/* Fire icon for hot streaks */}
            {streak.is_hot && (
              <span className="text-2xl">🔥</span>
            )}
            
            {/* Category & Record */}
            <div>
              <p className="text-zinc-400 text-xs uppercase tracking-wide">
                {streak.label || 'Overall'}
              </p>
              <p className={`font-mono text-xl font-black ${
                streak.is_hot 
                  ? 'text-orange-400' 
                  : streak.win_rate >= 60 
                  ? 'text-green-400' 
                  : 'text-white'
              }`}>
                {streak.wins}-{streak.losses}
              </p>
            </div>
            
            {/* Profit */}
            <div className="border-l border-white/10 pl-3">
              <p className="text-zinc-500 text-xs">Profit</p>
              <p className={`font-mono text-lg font-bold ${
                streak.profit >= 0 ? 'text-green-400' : 'text-red-400'
              }`}>
                {formatProfit(streak.profit)}
              </p>
            </div>
            
            {/* Win Rate & Last N */}
            <div className="border-l border-white/10 pl-3 text-center">
              <p className={`font-mono font-bold ${
                streak.win_rate >= 70 ? 'text-green-400' : 'text-cyan-400'
              }`}>
                {streak.win_rate}%
              </p>
              <p className="text-zinc-500 text-xs">Last {streak.n}</p>
            </div>
          </motion.div>
        ))}
      </div>
    </motion.div>
  );
};

// Export for use in other pages
export { HotStreaksDisplay };

// Premium Hero Section - Sells the subscription service
const PremiumHero = ({ onGetStarted }) => {
  const [trackRecord, setTrackRecord] = useState(null);
  const [animatedProfit, setAnimatedProfit] = useState(0);
  const [visitorCount, setVisitorCount] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const [recordRes, visitorRes] = await Promise.all([
          fetchWithRetry(`${API}/track-record`),
          fetchWithRetry(`${API}/visitor-count`),
        ]);
        setTrackRecord(recordRes.data);
        setVisitorCount(visitorRes.data.count);
        
        // Increment visitor count if this is a new session
        const hasVisited = sessionStorage.getItem('hsl_visited');
        if (!hasVisited) {
          sessionStorage.setItem('hsl_visited', 'true');
          try {
            const incrementRes = await axios.post(`${API}/visitor-count/increment`);
            setVisitorCount(incrementRes.data.count);
          } catch (e) {
            console.warn("Could not increment visitor count");
          }
        }
      } catch (error) {
        console.warn("LandingPage: Failed to fetch stats:", error.message);
      }
    };
    fetchStats();
  }, []);

  // Animate the profit counter
  useEffect(() => {
    if (trackRecord?.total_profit !== undefined) {
      const target = trackRecord.total_profit;
      const duration = 2000;
      const steps = 60;
      const increment = target / steps;
      let current = 0;
      const timer = setInterval(() => {
        current += increment;
        if ((target >= 0 && current >= target) || (target < 0 && current <= target)) {
          setAnimatedProfit(target);
          clearInterval(timer);
        } else {
          setAnimatedProfit(target >= 0 ? Math.floor(current) : Math.ceil(current));
        }
      }, duration / steps);
      return () => clearInterval(timer);
    }
  }, [trackRecord]);

  const currentProfit = trackRecord?.total_profit || 0;

  return (
    <section className="relative min-h-screen flex items-center pt-16 overflow-hidden" data-testid="premium-hero">
      {/* Background effects */}
      <div className="absolute inset-0 bg-gradient-to-b from-hsl-bg via-hsl-bg to-hsl-surface" />
      <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-green-500/10 rounded-full blur-3xl" />
      <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-yellow-500/10 rounded-full blur-3xl" />
      
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16 relative z-10 w-full">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="text-center"
        >
          {/* HSL Logo */}
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6 }}
            className="mb-8"
          >
            <img 
              src="/hsl-logo.png" 
              alt="Hammer Sports Logic" 
              className="w-64 sm:w-80 md:w-96 mx-auto drop-shadow-2xl"
            />
          </motion.div>

          {/* Achievement Badge */}
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.2, duration: 0.5 }}
            className="mb-6"
          >
            <Link 
              to="/road-to-100k"
              className="inline-flex items-center gap-2 bg-green-500/10 border border-green-500/40 rounded-full px-5 py-2.5 hover:bg-green-500/20 transition-all group"
            >
              <Trophy className="w-5 h-5 text-green-400" />
              <span className="text-green-400 font-semibold">$100K Goal Achieved!</span>
              <ArrowRight className="w-4 h-4 text-green-400 group-hover:translate-x-1 transition-transform" />
            </Link>
          </motion.div>

          {/* Main Title */}
          <motion.div
            initial={{ scale: 0.9 }}
            animate={{ scale: 1 }}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="mb-8"
          >
            <h1 className="font-heading text-5xl sm:text-6xl md:text-7xl lg:text-8xl font-black uppercase tracking-tighter text-white leading-none">
              Expert Sports{" "}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-hsl-primary via-cyan-400 to-hsl-primary">
                Analysis
              </span>
            </h1>
            <p className="text-xl md:text-2xl text-zinc-400 mt-4 max-w-2xl mx-auto">
              Data-driven picks backed by $100K+ in verified profit. Join the winning team.
            </p>
          </motion.div>

          {/* Profit YTD Display - Large & Prominent */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4, duration: 0.6 }}
            className="mb-10"
          >
            <div className="inline-block bg-gradient-to-br from-hsl-surface via-hsl-surface to-green-500/10 border border-white/20 rounded-2xl p-8 md:p-10">
              <p className="text-zinc-400 uppercase tracking-widest text-sm mb-2">Profit YTD</p>
              <div className="flex items-center justify-center gap-2">
                <span className={`font-mono text-5xl sm:text-6xl md:text-7xl font-black ${currentProfit >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                  {currentProfit >= 0 ? '+' : ''}${Math.abs(animatedProfit).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </span>
              </div>
              <div className="flex justify-center gap-8 mt-4 pt-4 border-t border-white/10">
                <div className="text-center">
                  <p className="font-mono text-2xl font-bold text-white">{trackRecord?.win_rate || 0}%</p>
                  <p className="text-zinc-500 text-xs uppercase">Win Rate</p>
                </div>
                <div className="text-center">
                  <p className="font-mono text-2xl font-bold text-white">{trackRecord?.total_picks || 0}</p>
                  <p className="text-zinc-500 text-xs uppercase">Total Picks</p>
                </div>
                <div className="text-center">
                  <p className="font-mono text-2xl font-bold text-white">{trackRecord?.roi || 0}%</p>
                  <p className="text-zinc-500 text-xs uppercase">ROI</p>
                </div>
              </div>
              {/* Prop Specialist Badge - Shows when prop win rate >= 60% and at least 5 props */}
              {trackRecord?.prop_breakdown?.props?.win_rate >= 60 && trackRecord?.prop_breakdown?.props?.total >= 5 && (
                <div className="mt-4 pt-4 border-t border-white/10 flex justify-center" data-testid="hero-prop-specialist">
                  <div className="bg-gradient-to-r from-purple-600 to-pink-500 text-white text-sm font-bold px-4 py-2 rounded-full flex items-center gap-2 shadow-lg shadow-purple-500/30">
                    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                    PROP SPECIALIST • {trackRecord.prop_breakdown.props.win_rate}% Win Rate
                  </div>
                </div>
              )}
            </div>
          </motion.div>

          {/* Free Trial Promo */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6, duration: 0.5 }}
            className="mb-8"
          >
            <div className="inline-block bg-gradient-to-r from-hsl-primary/10 to-cyan-500/10 border border-hsl-primary/30 rounded-xl px-6 py-4">
              <p className="text-hsl-primary font-heading font-bold text-lg md:text-xl uppercase tracking-wide">
                🎁 7-Day Free Trial Available
              </p>
              <p className="text-zinc-400 text-sm mt-1">
                Use code <span className="text-hsl-primary font-mono font-bold">HSLTRIAL</span> at checkout
              </p>
            </div>
          </motion.div>

          {/* CTA Buttons */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.8 }}
            className="flex flex-col sm:flex-row gap-4 justify-center"
          >
            <Button
              onClick={() => navigate('/pricing')}
              className="btn-primary-glow px-8 py-6 text-lg rounded-lg inline-flex items-center justify-center gap-2"
              data-testid="hero-pricing-btn"
            >
              Start Free Trial <ArrowRight size={20} />
            </Button>
            <Link to="/track-record">
              <Button
                variant="outline"
                className="bg-white/5 border-white/10 text-white hover:bg-white/10 px-8 py-6 text-lg rounded-lg w-full sm:w-auto"
              >
                View Results
              </Button>
            </Link>
          </motion.div>

          {/* Trust indicators */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1 }}
            className="mt-10 flex flex-wrap justify-center gap-6 text-zinc-500 text-sm"
          >
            <span className="flex items-center gap-2">
              <Check className="w-4 h-4 text-green-400" /> Every pick timestamped
            </span>
            <span className="flex items-center gap-2">
              <Check className="w-4 h-4 text-green-400" /> Verified results
            </span>
            <span className="flex items-center gap-2">
              <Check className="w-4 h-4 text-green-400" /> Cancel anytime
            </span>
          </motion.div>

          {/* Visitor Counter */}
          {visitorCount && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1.2 }}
              className="mt-8"
              data-testid="visitor-counter"
            >
              <div className="inline-flex items-center gap-2 bg-white/5 border border-white/10 rounded-full px-5 py-2.5">
                <div className="flex items-center gap-1.5">
                  <span className="relative flex h-2.5 w-2.5">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
                    <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-green-500"></span>
                  </span>
                </div>
                <span className="text-zinc-300 text-sm">
                  <span className="font-bold text-white">{visitorCount.toLocaleString()}</span> people have joined HSL.bet
                </span>
              </div>
            </motion.div>
          )}
        </motion.div>
      </div>
    </section>
  );
};


const HeroSection = ({ onGetStarted }) => {
  const [trackRecord, setTrackRecord] = useState(null);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const response = await axios.get(`${API}/track-record`);
        setTrackRecord(response.data);
      } catch (error) {
        console.error("Failed to fetch track record");
      }
    };
    fetchStats();
  }, []);

  return (
    <section className="hero-bg min-h-screen flex items-center pt-16">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 md:py-32">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <p className="text-hsl-primary font-heading font-bold uppercase tracking-widest mb-4" data-testid="hero-tagline">
              Drop The Hammer
            </p>
            <h1 className="font-heading text-5xl sm:text-6xl md:text-7xl font-bold uppercase tracking-tight text-white leading-none mb-6" data-testid="hero-title">
              Expert Sports<br />
              <span className="text-hsl-primary">Handicapping</span>
            </h1>
            <p className="text-lg text-zinc-400 leading-relaxed mb-8 max-w-lg" data-testid="hero-description">
              Data-driven picks across NFL, NBA, MLB, NHL, Soccer, UFC & more. 
              Join thousands of winners who trust HSL for sharp betting analysis.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Button
                onClick={onGetStarted}
                className="btn-primary-glow px-8 py-4 text-lg rounded-sm inline-flex items-center justify-center gap-2"
                data-testid="hero-cta-btn"
              >
                Start Winning <ArrowRight size={20} />
              </Button>
              <Link to="/track-record">
                <Button
                  variant="outline"
                  className="bg-white/5 border-white/10 text-white hover:bg-white/10 px-8 py-4 text-lg rounded-sm w-full sm:w-auto"
                  data-testid="hero-track-record-btn"
                >
                  View Results
                </Button>
              </Link>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="relative"
          >
            <div className="bg-hsl-surface border border-white/10 rounded-2xl p-8 relative overflow-hidden" data-testid="hero-stats-card">
              <div className="absolute top-0 right-0 w-64 h-64 bg-hsl-primary/10 rounded-full blur-3xl" />
              <h3 className="font-heading text-xl uppercase tracking-wide text-zinc-400 mb-6">Season Stats</h3>
              <div className="grid grid-cols-2 gap-6 relative z-10">
                <div>
                  <p className="text-zinc-500 text-sm uppercase tracking-wide mb-1">Win Rate</p>
                  <p className="font-mono text-4xl text-hsl-primary font-bold" data-testid="stat-win-rate">
                    {trackRecord ? `${trackRecord.win_rate}%` : "67.2%"}
                  </p>
                </div>
                <div>
                  <p className="text-zinc-500 text-sm uppercase tracking-wide mb-1">Profit</p>
                  <p className="font-mono text-4xl text-green-400 font-bold" data-testid="stat-profit">
                    {trackRecord ? `$${trackRecord.total_profit >= 0 ? '' : '-'}${Math.abs(trackRecord.total_profit).toLocaleString()}` : "$4,250"}
                  </p>
                </div>
                <div>
                  <p className="text-zinc-500 text-sm uppercase tracking-wide mb-1">Total Picks</p>
                  <p className="font-mono text-4xl text-white font-bold" data-testid="stat-total-picks">
                    {trackRecord ? trackRecord.total_picks : "127"}
                  </p>
                </div>
                <div>
                  <p className="text-zinc-500 text-sm uppercase tracking-wide mb-1">ROI</p>
                  <p className="font-mono text-4xl text-hsl-secondary font-bold" data-testid="stat-roi">
                    {trackRecord ? `${trackRecord.roi}%` : "+18.3%"}
                  </p>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

const SportsSection = () => {
  const sports = [
    { name: "NFL", icon: "🏈", color: "from-orange-500/20" },
    { name: "NBA", icon: "🏀", color: "from-orange-600/20" },
    { name: "MLB", icon: "⚾", color: "from-red-500/20" },
    { name: "NHL", icon: "🏒", color: "from-blue-500/20" },
    { name: "Soccer", icon: "⚽", color: "from-green-500/20" },
    { name: "UFC", icon: "🥊", color: "from-red-600/20" },
    { name: "NCAAF", icon: "🏟️", color: "from-purple-500/20" },
    { name: "NCAAB", icon: "🏀", color: "from-blue-600/20" },
    { name: "Tennis", icon: "🎾", color: "from-yellow-500/20" },
    { name: "PGA", icon: "⛳", color: "from-green-600/20" },
  ];

  return (
    <section className="py-20 md:py-32 bg-hsl-bg" data-testid="sports-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="font-heading text-4xl md:text-5xl font-bold uppercase tracking-tight text-white mb-4" data-testid="sports-title">
            All Major Sports Covered
          </h2>
          <p className="text-zinc-400 text-lg max-w-2xl mx-auto">
            Expert analysis across every league. From NFL spreads to UFC props, we've got you covered.
          </p>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
          {sports.map((sport, index) => (
            <motion.div
              key={sport.name}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1 }}
              className="sport-icon-wrapper bg-hsl-surface border border-white/5 p-6 text-center cursor-pointer"
              data-testid={`sport-${sport.name.toLowerCase()}`}
            >
              <span className="text-4xl mb-3 block">{sport.icon}</span>
              <p className="font-heading font-bold text-lg uppercase tracking-wide text-white">{sport.name}</p>
            </motion.div>
          ))}
        </div>
        
        {/* CTA to view picks */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mt-12"
        >
          <Link to="/dashboard">
            <Button className="btn-primary-glow px-8 py-4 text-lg rounded-sm inline-flex items-center gap-2">
              View All Recent Picks <ArrowRight size={20} />
            </Button>
          </Link>
        </motion.div>
      </div>
    </section>
  );
};

const AboutSection = () => {
  const [trackRecord, setTrackRecord] = useState(null);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const response = await axios.get(`${API}/track-record`);
        setTrackRecord(response.data);
      } catch (error) {
        console.error("Failed to fetch track record for about section");
      }
    };
    fetchStats();
  }, []);

  return (
    <section className="py-20 md:py-32 bg-hsl-surface" data-testid="about-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-16">
          {/* Welcome Section */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="font-heading text-4xl md:text-5xl font-bold uppercase tracking-tight text-white mb-6">
              Welcome to<br />
              <span className="text-hsl-primary">Hammer Sports Logic</span>
            </h2>
            <p className="text-xl text-white font-semibold mb-6">
              The sports handicapping industry is fundamentally broken. We are here to fix it.
            </p>
            <div className="space-y-4 text-zinc-400 leading-relaxed">
              <p>
                Welcome to a platform built for sports market investors, not gamblers. At Hammer Sports Logic (HSL), we don't deal in gut feelings, "guaranteed locks," or rented Lamborghinis. We treat the betting markets with the exact same ruthless, data-driven precision as Wall Street day trading.
              </p>
              <p>
                We find the edges the public completely misses. While casual bettors are blindly betting on team logos and prime-time narratives, we are breaking down drop-coverage defensive schemes, live-ball turnover rates, altitude-adjusted fatigue metrics, and zero-day rest rotational mismatches. We don't just give you a pick; we give you the deep, insider-level mathematical thesis behind it.
              </p>
            </div>
            <div className="mt-8 p-6 bg-hsl-bg/50 border-l-4 border-hsl-primary rounded-r-lg">
              <p className="font-heading text-2xl font-bold uppercase text-white">
                Our Philosophy is Simple:
              </p>
              <p className="font-heading text-3xl font-bold uppercase text-hsl-primary mt-2">
                Logic Over Luck.
              </p>
            </div>
          </motion.div>

          {/* Road to 100K Section */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <div className="bg-hsl-bg border border-white/10 rounded-2xl p-8 h-full">
              <div className="flex items-center gap-3 mb-6">
                <TrendingUp className="w-10 h-10 text-hsl-primary" />
                <h3 className="font-heading text-3xl md:text-4xl font-bold uppercase text-white">
                  The Road to $100K
                </h3>
              </div>
              <p className="text-xl text-hsl-primary font-semibold mb-6">
                We don't ask for your trust. We prove it on the ledger.
              </p>
              <div className="space-y-4 text-zinc-400 leading-relaxed">
                <p>
                  "The Road to 100K" is our flagship live case study and the ultimate proof of concept. We are actively turning a strictly managed bankroll into $100,000 of pure, verified profit—and we are doing it completely in public.
                </p>
                <p>
                  Every single ticket, every dollar wagered, every massive win, and every brutal bad beat is documented on our master ledger. We refuse to hide our red days like the rest of the industry. Instead, we show you exactly how our strict, money management system—featuring our maximum-confidence <span className="text-hsl-primary font-bold">HAMMER</span> plays—absorbs variance and grinds out long-term, undeniable profit.
                </p>
              </div>
              
              {/* Track Record Preview */}
              {trackRecord && (
                <div className="mt-8 p-5 bg-hsl-surface rounded-xl border border-white/10">
                  <p className="text-xs uppercase tracking-widest text-zinc-500 mb-4">Live Performance</p>
                  <div className="grid grid-cols-3 gap-4 mb-4">
                    <div className="text-center">
                      <p className="font-mono text-2xl font-bold text-hsl-primary">{trackRecord.win_rate}%</p>
                      <p className="text-xs text-zinc-500 uppercase">Win Rate</p>
                    </div>
                    <div className="text-center">
                      <p className={`font-mono text-2xl font-bold ${trackRecord.total_profit >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                        ${Math.abs(trackRecord.total_profit).toLocaleString()}
                      </p>
                      <p className="text-xs text-zinc-500 uppercase">Profit</p>
                    </div>
                    <div className="text-center">
                      <p className="font-mono text-2xl font-bold text-white">{trackRecord.total_picks}</p>
                      <p className="text-xs text-zinc-500 uppercase">Picks</p>
                    </div>
                  </div>
                  <Link to="/track-record" className="block">
                    <Button className="w-full bg-hsl-primary/10 hover:bg-hsl-primary/20 text-hsl-primary border border-hsl-primary/30 rounded-lg py-2 text-sm font-semibold uppercase tracking-wide">
                      View Full Results <ArrowRight className="inline w-4 h-4 ml-2" />
                    </Button>
                  </Link>
                </div>
              )}

              <p className="text-center text-xl font-heading font-bold text-white mt-6 uppercase">
                Welcome to the winning side of the sportsbook.
              </p>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

const AnalysisSection = () => {
  const features = [
    {
      title: "Unmatched Depth",
      description: "We look past the surface-level trends to find the situational angles, travel fatigue, and coaching strategies that actually move the needle."
    },
    {
      title: "Intelligent Breakdowns",
      description: "Our writeups don't just tell you who to bet; they teach you how to see the board like a sharp."
    },
    {
      title: "Total Conviction",
      description: "We never force a play. When we lock in a pick, our analysis lays out exactly why the math and the matchups are in our favor."
    }
  ];

  return (
    <section className="py-20 md:py-32 bg-hsl-bg relative overflow-hidden" data-testid="analysis-section">
      {/* Background accents */}
      <div className="absolute top-0 left-0 w-96 h-96 bg-hsl-primary/5 rounded-full blur-3xl" />
      <div className="absolute bottom-0 right-0 w-96 h-96 bg-hsl-secondary/5 rounded-full blur-3xl" />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <h2 className="font-heading text-4xl md:text-5xl font-bold uppercase tracking-tight text-white mb-6">
            Why Our Analysis{" "}
            <span className="text-hsl-primary">Stands Alone</span>
          </h2>
          <p className="text-xl text-zinc-400 max-w-3xl mx-auto leading-relaxed">
            We built this platform on a simple premise: <span className="text-white font-semibold">logic over luck</span>. 
            That means every play we release is backed by a level of deep, analytical insight you won't find anywhere else.
          </p>
        </motion.div>

        <div className="grid md:grid-cols-3 gap-8">
          {features.map((feature, index) => (
            <motion.div
              key={feature.title}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.15, duration: 0.5 }}
              className="bg-hsl-surface/50 backdrop-blur-sm border border-white/5 rounded-2xl p-8 hover:border-hsl-primary/30 transition-colors group"
            >
              <div className="w-12 h-12 bg-hsl-primary/10 rounded-xl flex items-center justify-center mb-6 group-hover:bg-hsl-primary/20 transition-colors">
                <span className="text-hsl-primary font-heading font-bold text-xl">{index + 1}</span>
              </div>
              <h3 className="font-heading text-2xl font-bold uppercase text-white mb-4">
                {feature.title}
              </h3>
              <p className="text-zinc-400 leading-relaxed">
                {feature.description}
              </p>
            </motion.div>
          ))}
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.5, duration: 0.5 }}
          className="text-center mt-12"
        >
          <p className="text-lg text-zinc-300 font-medium">
            Read our daily breakdowns and see the difference for yourself.
          </p>
          <Link to="/dashboard">
            <Button className="mt-6 btn-primary-glow px-8 py-4 text-lg rounded-sm inline-flex items-center gap-2">
              View Today's Analysis <ArrowRight size={20} />
            </Button>
          </Link>
        </motion.div>
      </div>
    </section>
  );
};

const FeaturesSection = () => {
  const features = [
    {
      icon: <ChartBar className="w-8 h-8" />,
      title: "Data-Driven Analysis",
      description: "Every pick backed by advanced statistics, trends, and situational analysis."
    },
    {
      icon: <TrendingUp className="w-8 h-8" />,
      title: "Transparent Results",
      description: "Full transparency with verified results. Every pick tracked and documented."
    },
    {
      icon: <Zap className="w-8 h-8" />,
      title: "Real-Time Picks",
      description: "Get notified instantly when new picks drop. Never miss a sharp play."
    },
    {
      icon: <Shield className="w-8 h-8" />,
      title: "Bankroll Management",
      description: "Unit-based betting system designed for long-term profitability."
    },
    {
      icon: <Users className="w-8 h-8" />,
      title: "Community Access",
      description: "Join thousands of like-minded bettors. Share insights and strategies."
    },
    {
      icon: <Lock className="w-8 h-8" />,
      title: "Premium Plays",
      description: "Exclusive high-confidence picks reserved for VIP members only."
    }
  ];

  return (
    <section className="py-20 md:py-32 stats-grid" data-testid="features-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="font-heading text-4xl md:text-5xl font-bold uppercase tracking-tight text-white mb-4" data-testid="features-title">
            Why Choose HSL
          </h2>
          <p className="text-zinc-400 text-lg max-w-2xl mx-auto">
            We don't just pick winners. We build a sustainable betting approach.
          </p>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {features.map((feature, index) => (
            <motion.div
              key={feature.title}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1 }}
              className="bg-hsl-surface border border-white/5 p-8 rounded-xl card-hover group"
              data-testid={`feature-card-${index}`}
            >
              <div className="text-hsl-primary mb-4 group-hover:scale-110 transition-transform">
                {feature.icon}
              </div>
              <h3 className="font-heading text-xl font-bold uppercase tracking-wide text-white mb-2">
                {feature.title}
              </h3>
              <p className="text-zinc-400 leading-relaxed">
                {feature.description}
              </p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

const CTASection = ({ onGetStarted }) => {
  return (
    <section className="py-20 md:py-32 cta-gradient relative" data-testid="cta-section">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
        <Trophy className="w-16 h-16 text-hsl-primary mx-auto mb-6" />
        <h2 className="font-heading text-4xl md:text-5xl font-bold uppercase tracking-tight text-white mb-4" data-testid="cta-title">
          Ready to Start Winning?
        </h2>
        <p className="text-zinc-400 text-lg mb-8 max-w-2xl mx-auto">
          Join HSL today and get access to expert picks, detailed analysis, and a proven track record of success.
        </p>
        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <Button
            onClick={onGetStarted}
            className="btn-primary-glow px-8 py-4 text-lg rounded-sm inline-flex items-center justify-center gap-2"
            data-testid="cta-btn"
          >
            Get Started Free <ArrowRight size={20} />
          </Button>
          <Link to="/pricing">
            <Button
              variant="outline"
              className="bg-white/5 border-white/10 text-white hover:bg-white/10 px-8 py-4 text-lg rounded-sm"
              data-testid="cta-pricing-btn"
            >
              View Pricing
            </Button>
          </Link>
        </div>
      </div>
    </section>
  );
};

const Footer = () => {
  return (
    <footer className="bg-hsl-bg border-t border-white/5 py-12" data-testid="footer">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid md:grid-cols-5 gap-8">
          <div>
            <Link to="/" className="flex items-center gap-2 mb-4">
              <img src="/hsl-logo.png" alt="HSL.bet" className="h-16 w-auto" />
            </Link>
            <p className="text-zinc-500 text-sm">
              Logic over luck. Expert sports handicapping backed by data and transparency.
            </p>
          </div>
          <div>
            <h4 className="font-heading font-bold uppercase tracking-wide text-white mb-4">Quick Links</h4>
            <ul className="space-y-2">
              <li><Link to="/dashboard" className="footer-link">Today's Picks</Link></li>
              <li><Link to="/track-record" className="footer-link">Results</Link></li>
              <li><Link to="/pricing" className="footer-link">Pricing</Link></li>
              <li><Link to="/hammer-talk" className="footer-link">Hammer Talk</Link></li>
              <li><Link to="/updates" className="footer-link">Site Updates</Link></li>
            </ul>
          </div>
          <div>
            <h4 className="font-heading font-bold uppercase tracking-wide text-white mb-4">Sports</h4>
            <ul className="space-y-2">
              <li><span className="footer-link cursor-pointer">NFL</span></li>
              <li><span className="footer-link cursor-pointer">NBA</span></li>
              <li><span className="footer-link cursor-pointer">MLB</span></li>
              <li><span className="footer-link cursor-pointer">NHL</span></li>
            </ul>
          </div>
          <div>
            <h4 className="font-heading font-bold uppercase tracking-wide text-white mb-4">Legal</h4>
            <ul className="space-y-2">
              <li><span className="footer-link cursor-pointer">Terms of Service</span></li>
              <li><span className="footer-link cursor-pointer">Privacy Policy</span></li>
              <li><span className="footer-link cursor-pointer">Responsible Gaming</span></li>
            </ul>
          </div>
          <div>
            <h4 className="font-heading font-bold uppercase tracking-wide text-white mb-4">Contact</h4>
            <ul className="space-y-2">
              <li>
                <a href="mailto:support@hsl.bet" className="footer-link flex items-center gap-2">
                  <span>📧</span> support@hsl.bet
                </a>
              </li>
            </ul>
          </div>
        </div>
        <div className="border-t border-white/5 mt-8 pt-8 text-center">
          <p className="text-zinc-600 text-sm">
            © 2026 Hammer Sports Logic. All rights reserved. Must be 21+ to participate. Please bet responsibly.
          </p>
        </div>
      </div>
    </footer>
  );
};

const LandingPage = () => {
  const [authModalOpen, setAuthModalOpen] = useState(false);
  const [authMode, setAuthMode] = useState("register");
  const { user } = useAuth();
  const navigate = useNavigate();

  const handleGetStarted = () => {
    if (user) {
      navigate("/dashboard");
    } else {
      setAuthMode("register");
      setAuthModalOpen(true);
    }
  };

  const handleLoginClick = () => {
    setAuthMode("login");
    setAuthModalOpen(true);
  };

  const handleRegisterClick = () => {
    setAuthMode("register");
    setAuthModalOpen(true);
  };

  return (
    <div data-testid="landing-page">
      <SEO 
        title="Expert Sports Picks & Analysis"
        description="Free expert sports picks with a verified track record. Join the Road to $100K with data-driven NFL, NBA, MLB, NHL analysis. Logic over luck. 60%+ win rate."
        keywords="sports picks, sports betting, handicapping, expert picks, free sports picks, NFL picks, NBA picks, MLB picks, NHL picks, betting analysis, HAMMER picks, Road to 100K"
        canonical="/"
      />
      <Navbar onLoginClick={handleLoginClick} onRegisterClick={handleRegisterClick} />
      <PremiumHero onGetStarted={handleGetStarted} />
      <SportsSection />
      <AboutSection />
      <AnalysisSection />
      <FeaturesSection />
      <CTASection onGetStarted={handleGetStarted} />
      <Footer />
      <AuthModal
        isOpen={authModalOpen}
        onClose={() => setAuthModalOpen(false)}
        mode={authMode}
        setMode={setAuthMode}
      />
    </div>
  );
};

export default LandingPage;
